/* eslint-disable no-console */
import { register } from 'register-service-worker'
import { ref } from 'vue'

// Single source of truth for the "a new version is waiting" state.
// App.vue binds the update modal directly to this ref.
export const updateAvailable = ref(false);

// The current ServiceWorkerRegistration (set once registered / updated).
export let swRegistration = null;

// Guard so we only ever reload the page once when a new SW takes control.
let refreshing = false;

// When the waiting SW becomes the active controller (after SKIP_WAITING),
// reload exactly once so the page runs the freshly precached assets.
if ('serviceWorker' in navigator && navigator.serviceWorker) {
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) return;
    refreshing = true;
    window.location.reload();
  });
}

// Read-only status helper (kept for existing consumers).
export const getServiceWorkerStatus = () => {
  return {
    registration: swRegistration,
    updateAvailable: updateAvailable.value,
  };
};

// Apply the pending update: tell the waiting SW to skip waiting.
// The controllerchange listener above performs the single reload.
export const applyUpdate = () => {
  updateAvailable.value = false;
  const waiting = swRegistration && swRegistration.waiting;
  if (waiting) {
    waiting.postMessage({ type: 'SKIP_WAITING' });
  } else if (!refreshing) {
    // Edge case: no waiting worker (e.g. already activated). Reload once.
    refreshing = true;
    window.location.reload();
  }
};

// Force an immediate update check (used by the router on navigation).
export const forceServiceWorkerUpdate = async () => {
  try {
    if (swRegistration) {
      return await swRegistration.update();
    }
    return null;
  } catch (error) {
    console.error('Failed to force service worker update:', error);
    return null;
  }
};

// Unregister and re-register the SW (used by the push debugger tooling).
export const clearAndReRegisterServiceWorkers = async () => {
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
    await new Promise((resolve) => setTimeout(resolve, 1000));

    if (process.env.NODE_ENV === 'production') {
      register(`${process.env.BASE_URL}service-worker.js`, {
        registered(registration) {
          swRegistration = registration;
        },
        error(error) {
          console.error('Error re-registering service worker:', error);
        },
      });
    }
    return true;
  } catch (error) {
    console.error('Error clearing and re-registering service workers:', error);
    return false;
  }
};

if (process.env.NODE_ENV === 'production') {
  register(`${process.env.BASE_URL}service-worker.js`, {
    ready() {
      console.log('App is being served from cache by a service worker.');
    },
    registered(registration) {
      console.log('Service worker has been registered.');
      swRegistration = registration;

      // Single, low-frequency update check. Workbox/browser also re-fetch the
      // SW on navigation; this catches long-lived tabs without hammering.
      const UPDATE_INTERVAL = 30 * 60 * 1000; // 30 minutes

      const checkForUpdates = () => {
        registration.update().catch((error) => {
          console.log('Service worker update check failed:', error);
        });
      };

      setInterval(checkForUpdates, UPDATE_INTERVAL);

      // Also check when the user returns to the tab.
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
          checkForUpdates();
        }
      });
    },
    cached() {
      console.log('Content has been cached for offline use.');
    },
    updatefound() {
      console.log('New content is downloading.');
    },
    updated(registration) {
      console.log('New content is available; prompting user to refresh.');
      swRegistration = registration;
      // Only signal — the user decides when to apply via the modal.
      updateAvailable.value = true;
    },
    offline() {
      console.log('No internet connection found. App is running in offline mode.');
    },
    error(error) {
      console.error('Error during service worker registration:', error);
    },
  });
} else {
  // Development mode - disable service worker to avoid stale caches.
  if ('serviceWorker' in navigator && navigator.serviceWorker) {
    navigator.serviceWorker.getRegistrations().then(function (registrations) {
      for (let registration of registrations) {
        registration.unregister();
      }
    });
  }
  console.log('Service Worker disabled in development mode');
}
